<script>
import { nextTick } from 'vue';

import T from './JobagentApp.i18n.js';

import DsAlert from 'JIX/components/ds/DsAlert.vue';
import JobagentCard from 'JIX/components/jobagent/JobagentCard.vue';
import JobagentCreatedConfirmation from 'JIX/components/jobagent/JobagentCreatedConfirmation.vue';
import JobagentForm from 'JIX/components/jobagent/JobagentForm.vue';

import bootbox from 'bootbox';

import { mapGetters, mapState } from 'vuex';

const TABS = Object.freeze({
    all: {
        label: T.all(),
        emptyText: T.allEmpty(),
        filter: () => true,
    },
    active: {
        label: T.active(),
        emptyText: T.activeEmpty(),
        filter: (x) => x.enabled,
    },
    inactive: {
        label: T.inactive(),
        emptyText: T.inactiveEmpty(),
        filter: (x) => !x.enabled,
    },
});

const locale = document.querySelector('html').getAttribute('lang') || 'da-DK';
const sorter = (a, b) => a.display_name.localeCompare(b.display_name, locale);

const SCROLL_ARGS = { behavior: 'smooth' };

export default {
    components: {
        DsAlert,
        JobagentCard,
        JobagentCreatedConfirmation,
        JobagentForm,
    },
    data() {
        return {
            T,
            TABS,
            activeTab: "all",
            editingJobagent: null,
            formActive: this.$store.getters.jobagentList.length === 0, // Show form on load if user has no jobagents
            email: '',
            explain: null,
        };
    },
    computed: {
        ...mapState(['isLoggedIn', 'duplicateJobagent']),
        ...mapGetters(['jobagentList']),
        shownJobagents() {
            return this.jobagentsByTab[this.activeTab];
        },
        jobagentsByTab() {
            return Object.keys(TABS).reduce((res, tab) => {
                res[tab] = this.jobagentList.filter(TABS[tab].filter).sort(sorter);
                return res;
            }, {});
        },
        formTitle() {
            return this.editingJobagent ? T.editJobagent() : T.createNew();
        },
        pageFragment() {
            const parts = [];
            if (this.editingJobagent) {
                parts.push("edit=" + this.editingJobagent.id);
            }
            if (this.explain) {
                parts.push("explain=" + this.explain);
            }
            return parts.join(";");
        },
        hasOldCategories() {
            return this.jobagentList.filter((j) => j.data_names.old_categories.length > 0).length > 0;
        },
    },
    methods: {
        shouldConfirmLeave() {
            return this.formActive && this.$refs.form.isDirty();
        },
        newJobagent() {
            this.editingJobagent = null;
            this.formActive = true;
        },
        toggleJobagent(jobagent) {
            this.$store.dispatch('toggleJobagent', jobagent);
        },
        toggleFrequency(jobagent) {
            const newFrequency = jobagent.frequency === 'daily' ? 'weekly' : 'daily';
            this.$store.dispatch('setJobagentFrequency', { jobagent, newFrequency });
        },
        confirmLeave(callback) {
            if (!this.shouldConfirmLeave()) {
                callback();
                return;
            }
            bootbox.dialog({
                title: T.confirmSaveTitle(),
                message: T.confirmSaveMessage(),
                size: "large",
                onEscape: true,
                onBackdrop: true,
                buttons: {
                    cancel: {
                        label: T.cancel(),
                        className: 'btn-secondary',
                    },
                    continue: {
                        label: T.continueWithoutSaving(),
                        className: 'btn-secondary',
                        callback: callback,
                    },
                    save: {
                        label: T.saveChanges(),
                        className: 'btn-primary',
                        callback: () => this.$refs.form.save().then(callback),
                    },
                },
            });
        },
        editJobagent(jobagent) {
            this.confirmLeave(() => {
                this.editingJobagent = jobagent;
                this.formActive = true;
                nextTick().then(() => {
                    // [DHE] I don't know why this setTimeout is necessary, but for some reason it doesn't work properly
                    // on Mac Chrome without it. It seems that setting the key on the form somehow prevents it from doing
                    // the scrolling. I don't know why that is the case seeing as we are not scrolling to the form, but
                    // the title above it.
                    setTimeout(() => {
                        this.$refs.formTitle.scrollIntoView(SCROLL_ARGS);
                    }, 0);
                });
            });
        },
        deleteJobagent(jobagent) {
            const deletingEditing = this.editingJobagent && this.editingJobagent.id === jobagent.id;
            const confirmMessage = deletingEditing ? T.confirmDeleteCurrent() : T.confirmDelete();

            bootbox.confirm({
                message: confirmMessage,
                buttons: {
                    confirm: {
                        label: 'Slet Jobagent',
                        className: 'btn-danger'
                    },
                    cancel: {
                        label: 'Annuller',
                        className: 'btn-secondary'
                    }
                },
                callback: (result) => {
                    if (result) {
                        this.$store.dispatch('deleteJobagent', jobagent);
                        if (deletingEditing) {
                            this.handleSaved();
                        }
                    }
                }
            });
        },
        handleSaved(payload = {}) {
            const jobagent = this.editingJobagent;
            this.editingJobagent = null;
            this.formActive = false;
            this.email = payload.email;
            this.explain = null;
            if (this.isLoggedIn) {
                if (jobagent) {
                    this.viewJobagent(jobagent, false);
                } else {
                    nextTick().then(() => {
                        this.$refs.createButton.scrollIntoView(SCROLL_ARGS);
                    });
                }
            }
        },
        viewJobagent(jobagent, open = true) {
            if (!this.shownJobagents.some(x => x.id === jobagent.id)) {
                this.activeTab = 'all';
            }

            // We need to wait until next tick to make sure that the
            // card is in view in case we had to update the tab
            nextTick().then(() => {
                const card = this.$refs.cards.find(x => x.jobagentId === jobagent.id);
                if (open) {
                    card.open();
                }

                card.$el.scrollIntoView(SCROLL_ARGS);
            });
        },
        beforeUnloadHandler(event) {
            if (this.shouldConfirmLeave()) {
                event.preventDefault();
                event.returnValue = '';
            }
        },
    },
    mounted() {
        window.addEventListener('beforeunload', this.beforeUnloadHandler);

        const explainMatch = window.location.hash.match(/\bexplain=([\w-]+)\b/);
        if (explainMatch) {
            this.explain = explainMatch[1];
        }

        const editMatch = window.location.hash.match(/\bedit=(\d+)\b/);
        if (editMatch) {
            const id = editMatch[1];
            const jobagent = this.$store.state.jobagents[id];
            if (jobagent) {
                this.editJobagent(jobagent);
            }
        }

    },
    beforeUnmount() {
        window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    },
    watch: {
        pageFragment() {
            window.location.hash = this.pageFragment;
        },
    },
};
</script>

<template>
    <div>
        <DsAlert
            type="warning"
            @closed="$store.commit('clearDuplicateJobagent')"
            v-if="duplicateJobagent"
            :key="duplicateJobagent.id"
            dismissable
        >
            {{ T.duplicateWarning() }}
            <a href="#" @click.prevent="editJobagent(duplicateJobagent)">{{ duplicateJobagent.display_name }}</a>
        </DsAlert>
        <DsAlert
            type="warning"
            v-if="hasOldCategories"
        >
            {{ T.oldCategoriesWarning() }}
        </DsAlert>

        <template v-if="jobagentList.length > 0 && isLoggedIn">
            <ul class="nav nav-tabs-sm mb-4 mb-md-5" role="tablist">
                <li class="nav-item" v-for="tabKey in Object.keys(TABS)" :key="tabKey">
                    <a
                        :class="[ 'nav-link', { active: activeTab === tabKey } ]"
                        href="#"
                        @click.prevent="activeTab = tabKey"
                        role="tab"
                        :aria-selected="activeTab === tabKey ? 'true' : 'false'"
                        aria-controls="jobagent-card-list"
                    >{{ TABS[tabKey].label }}</a>
                    <span class="counter">{{ jobagentsByTab[tabKey].length }}</span>
                </li>
            </ul>

            <p class="text-muted mb-4 mb-md-5" v-if="shownJobagents.length === 0">
                {{ TABS[activeTab].emptyText }}
            </p>
            <div
                id="jobagent-card-list"
                class="row mb-4"
                role="list"
                v-else
            >
                <div class="col-md-6 mb-3" v-for="jobagent in shownJobagents" :key="jobagent.id">
                    <JobagentCard
                        :jobagent="jobagent"
                        @toggle-activate="toggleJobagent(jobagent)"
                        @toggle-frequency="toggleFrequency(jobagent)"
                        @edit="editJobagent(jobagent)"
                        @delete="deleteJobagent(jobagent)"
                        role="listitem"
                        ref="cards"
                    />
                </div>
            </div>
        </template>

        <template v-if="formActive">
            <h2 class="h3" ref="formTitle">
                {{ formTitle }}
            </h2>
            <JobagentForm
                source="overview"
                :is-new="!editingJobagent"
                :jobagent="editingJobagent"
                :key="this.editingJobagent ? 'jobagent-' + this.editingJobagent.id : 'new'"
                :is-logged-in="isLoggedIn"
                :explain="explain"
                @done="handleSaved"
                @view-jobagent="viewJobagent($event)"
                ref="form"
            />
        </template>
        <JobagentCreatedConfirmation
            :email="email"
            v-else-if="!isLoggedIn"
        />
        <button
            @click="newJobagent"
            type="button"
            class="btn btn-primary"
            ref="createButton"
            v-else
        >
            {{ T.createNew() }}
        </button>
    </div>
</template>
